import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CheckCircle, Cancel, Warning, ErrorOutline, CloudUpload, Edit } from '@material-ui/icons';
import { Tooltip } from '@material-ui/core';
import { greens, reds, oranges, greys } from '@/styles/colors';
import { differenceInCalendarDays, parseISO } from 'date-fns';

const EXPIRY_WARNING_DAYS = 30;

export const evaluateDocStatus = (doc) => {
    if (!doc) return { state: 'missing', daysToExpiry: null };
    if (!doc.expiration_date) return { state: 'valid', daysToExpiry: null };

    const expiresAt = typeof doc.expiration_date === 'string' ? parseISO(doc.expiration_date) : doc.expiration_date;
    const days = differenceInCalendarDays(expiresAt, new Date());

    if (days < 0) return { state: 'expired', daysToExpiry: days };
    if (days <= EXPIRY_WARNING_DAYS) return { state: 'expiring', daysToExpiry: days };
    return { state: 'valid', daysToExpiry: days };
};

const computeIconConfig = (status, doc, label) => {
    switch (status.state) {
        case 'valid':
            return {
                Icon: CheckCircle,
                color: greens.primary,
                statusTip: doc?.expiration_date
                    ? `${label} valid (expires in ${status.daysToExpiry}d)`
                    : `${label} on file`,
                actionVerb: 'replace',
            };
        case 'expiring':
            return {
                Icon: Warning,
                color: oranges.primary,
                statusTip: `${label} expires in ${status.daysToExpiry}d`,
                actionVerb: 'replace',
            };
        case 'expired':
            return {
                Icon: ErrorOutline,
                color: reds.primary,
                statusTip: `${label} expired ${Math.abs(status.daysToExpiry)}d ago`,
                actionVerb: 'replace',
            };
        case 'missing':
        default:
            return {
                Icon: Cancel,
                color: reds.primary,
                statusTip: `${label} missing`,
                actionVerb: 'upload',
            };
    }
};

const DocStatusIcon = ({ doc, label, fontSize = 'small', onClick }) => {
    const status = useMemo(() => evaluateDocStatus(doc), [doc]);
    const { Icon, color, statusTip, actionVerb } = useMemo(
        () => computeIconConfig(status, doc, label),
        [status, doc, label]
    );
    const tip = onClick ? `${statusTip} — click to ${actionVerb}` : statusTip;

    return (
        <Tooltip title={tip} placement="top">
            <Icon
                fontSize={fontSize}
                onClick={onClick}
                css={css`
                    color: ${color};
                    cursor: ${onClick ? 'pointer' : 'default'};
                `}
            />
        </Tooltip>
    );
};

export default DocStatusIcon;

// --- Generic colored pill (shared base for any status chip) ---

const tintFromColor = (hex, alpha = '14') => `${hex}${alpha}`; // hex w/ alpha suffix (#rrggbbAA)

export const colorTints = (hex) => ({
    $textColor: hex,
    $borderColor: tintFromColor(hex, '55'),
    $bgColor: tintFromColor(hex, '14'),
    $hoverBgColor: tintFromColor(hex, '24'),
});

export const ColoredPill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.4rem 0.85rem;
    border-radius: 999px;
    border: 1px solid ${(p) => p.$borderColor};
    background: ${(p) => p.$bgColor};
    color: ${(p) => p.$textColor};
    cursor: ${(p) => (p.onClick || p.$clickable ? 'pointer' : 'default')};
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    transition: background-color 120ms ease, border-color 120ms ease, transform 80ms ease;

    &:hover {
        background: ${(p) => p.$hoverBgColor};
        border-color: ${(p) => p.$textColor};
    }

    &:active {
        transform: scale(0.97);
    }

    &:focus-visible {
        outline: 2px solid ${(p) => p.$textColor};
        outline-offset: 1px;
    }
`;

// --- Pill button variant: explicit status + action label ---

export const DocActionPill = ({ doc, label, onClick, prefixLabel }) => {
    const status = useMemo(() => evaluateDocStatus(doc), [doc]);
    const {
        Icon: StatusIcon,
        color,
        statusTip,
        actionVerb,
    } = useMemo(() => computeIconConfig(status, doc, label), [status, doc, label]);

    const ActionIcon = actionVerb === 'upload' ? CloudUpload : Edit;
    const tip = `${statusTip} — click to ${actionVerb} ${label}`;
    const name = prefixLabel || label;

    return (
        <Tooltip title={tip} placement="top">
            <ColoredPill type="button" onClick={onClick} {...colorTints(color)}>
                <StatusIcon
                    css={css`
                        font-size: 16px;
                        color: ${color};
                    `}
                />
                <span>{name}</span>
                <ActionIcon
                    css={css`
                        font-size: 16px;
                        color: ${color};
                        margin-left: auto;
                    `}
                />
            </ColoredPill>
        </Tooltip>
    );
};

// --- Hover-affordance wrapper: keeps the bare icon but adds a clear "click target" surface ---

const HoverSurface = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    border: 1px solid transparent;
    background: transparent;
    color: ${greys[2]};
    cursor: pointer;
    font: inherit;
    transition: background-color 120ms ease, border-color 120ms ease;

    & .doc-action-hint {
        opacity: 0;
        transition: opacity 120ms ease;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &:hover {
        background: ${greys[5]};
        border-color: ${greys[4]};
    }

    &:hover .doc-action-hint {
        opacity: 1;
    }

    &:focus-visible {
        outline: 2px solid ${(p) => p.$accent};
        outline-offset: 1px;
    }
`;

export const DocStatusHoverButton = ({ doc, label, onClick }) => {
    const status = useMemo(() => evaluateDocStatus(doc), [doc]);
    const { color, statusTip, actionVerb } = useMemo(() => computeIconConfig(status, doc, label), [status, doc, label]);
    const tip = `${statusTip} — click to ${actionVerb} ${label}`;
    const actionLabel = actionVerb === 'upload' ? 'Upload' : 'Replace';

    return (
        <Tooltip title={tip} placement="top">
            <HoverSurface
                type="button"
                onClick={onClick}
                $accent={color}
                css={css`
                    color: ${color};
                `}
            >
                <DocStatusIcon doc={doc} label={label} />
                <span className="doc-action-hint">{actionLabel}</span>
            </HoverSurface>
        </Tooltip>
    );
};
